import { positiveValidDues, positiveValidDuesOf } from "./rule-vars.js";
import type { OrchestrationContext } from "./types.js";

/**
 * 🔴🔴 US-AI-0439-S03 (@UC9690) — TÍN HIỆU: những đại lượng mà điều kiện của flow được phép nhìn.
 *
 * Trước task này, mỗi luật là **một hàm TypeScript** trong `rule-engine.RULES` ⇒ thêm một trường hợp
 * phải sửa 5 file (union `RuleName`, `RULES`, catalog, `rule-vars`, loader). Đó đúng là thứ PO muốn bỏ
 * (*"ngắn gọn và linh hoạt, không phải xử lý từng case"*).
 *
 * Bảng này là **whitelist ĐÓNG** — cùng nguyên tắc với `TX_TYPES`/`AccountKind` (CLAUDE.md §3.8):
 *  - `SignalKey = keyof typeof SIGNALS` ⇒ gõ sai tên tín hiệu là **TS đỏ**, không phải một luật im
 *    lặng không bao giờ khớp (lỗi tệ nhất trong hệ nhắc tiền: không ai thấy nó *không* chạy);
 *  - mọi hàm ở đây **THUẦN + TẤT ĐỊNH**: không LLM, không DB, không `Date.now()` (DP-3);
 *  - không có "tín hiệu tuỳ ý" nào lọt vào bằng chuỗi.
 *
 * Tín hiệu SUY RA (`groupCount`, `positiveDueCount`…) cố ý nằm ở đây thay vì bắt catalog viết
 * `groups.length`: điều kiện phải là **dữ liệu so sánh được**, không phải mẩu mã.
 */
/** "Không có hạn chót nào" — xem docblock các tín hiệu `*HoursLeft`. */
export const NO_DEADLINE = Number.MAX_SAFE_INTEGER;

export const SIGNALS = Object.freeze({
  hasAnyGroup: (ctx: OrchestrationContext): boolean => ctx.hasAnyGroup,
  groupCount: (ctx: OrchestrationContext): number => ctx.groups.length,
  fundCount: (ctx: OrchestrationContext): number => ctx.funds.length,
  /**
   * Số khoản CÒN PHẢI ĐÓNG — dùng **đúng** luật của `positiveValidDues` (loại due mồ côi + loại
   * `<= 0`). Nếu ở đây tự đếm `memberDues.length` thì lại đẻ ra bản chép thứ hai của luật đọc nợ —
   * đúng cái bug đã đo ngày 17/08 (`selectPrimaryDue` docblock).
   */
  positiveDueCount: (ctx: OrchestrationContext): number => positiveValidDues(ctx).length,
  /**
   * 🔴 US-AI-0440-S04 (@UC9695) — số khoản NGƯỜI DÙNG ĐÃ BÁO CHUYỂN, đang chờ đối chiếu. Dùng cùng
   * luật lọc với nợ thường (`positiveValidDues`): quỹ phải có trong ctx và số tiền `> 0`.
   */
  awaitingConfirmationCount: (ctx: OrchestrationContext): number =>
    positiveValidDuesOf(ctx, ctx.awaitingConfirmationDues ?? []).length,
  pendingConfirmations: (ctx: OrchestrationContext): number => ctx.pendingConfirmations,
  openVotes: (ctx: OrchestrationContext): number => ctx.openVotes,
  isFirstAiWrite: (ctx: OrchestrationContext): boolean => ctx.isFirstAiWrite,
  stalePendingProposals: (ctx: OrchestrationContext): number => ctx.stalePendingProposals ?? 0,
  /** Chip người dùng vừa bấm; không bấm gì ⇒ `null` (KHÔNG chuỗi rỗng — rỗng vẫn là "có bấm"). */
  /** Còn nợ người↔người (nguồn: `FinancialPositionService.hasOpenPeerDebt`; hiếu hỉ KHÔNG tính). */
  /**
   * 🔴 US-AI-0440-S01 (@UC9692) — trạng thái KHOÁ. Nhóm `suspended` chặn WRITE (READ vẫn mở); quỹ
   * `frozen` khiến mọi thao tác tiền trả 409. `0438` đã bỏ nút ở các thẻ đó — ba tín hiệu này là vế
   * **nói ra lý do**, để người dùng không tưởng app hỏng.
   */
  hasSuspendedGroup: (ctx: OrchestrationContext): boolean =>
    ctx.groups.some((g) => g.status === "suspended"),
  hasFrozenFund: (ctx: OrchestrationContext): boolean =>
    ctx.funds.some((f) => f.status === "frozen"),
  /** Có nhóm nhưng KHÔNG rút được vai — engine không biết người này được làm gì. */
  roleUnknownInGroup: (ctx: OrchestrationContext): boolean =>
    ctx.role === null && ctx.hasAnyGroup,
  hasPeerDebt: (ctx: OrchestrationContext): boolean => ctx.hasPeerDebt === true,
  /**
   * 🔴 US-AI-0440-S03 (@UC9694) — GIỜ CÒN LẠI tới hạn cứng.
   *
   * Vắng ⇒ `NO_DEADLINE` (một số rất lớn) chứ **không phải 0**: điều kiện luôn là `còn ≤ ngưỡng`, nên
   * trả 0 khi KHÔNG có hạn nào sẽ khiến mọi người dùng nhận cảnh báo cho việc không tồn tại. Đây là
   * chỗ dễ sai nhất của cả subtask nên viết thành hằng có tên, không để `?? 0` trôi trong code.
   */
  coolingExpenseHoursLeft: (ctx: OrchestrationContext): number =>
    ctx.coolingExpenseHoursLeft ?? NO_DEADLINE,
  voteDeadlineHoursLeft: (ctx: OrchestrationContext): number =>
    ctx.voteDeadlineHoursLeft ?? NO_DEADLINE,
  ownerTransferObjectionHoursLeft: (ctx: OrchestrationContext): number =>
    ctx.ownerTransferObjectionHoursLeft ?? NO_DEADLINE,
  ownerTransferTargetsMe: (ctx: OrchestrationContext): boolean =>
    ctx.ownerTransferTargetsMe === true,
  hasDraftForm: (ctx: OrchestrationContext): boolean => ctx.hasDraftForm === true,
  hasConfirmPendingForm: (ctx: OrchestrationContext): boolean => ctx.hasConfirmPendingForm === true,
  pendingJoinRequests: (ctx: OrchestrationContext): number => ctx.pendingJoinRequests ?? 0,
  /**
   * 🔴 US-AI-0441-S01 (@UC9696) — ba lối vào nhóm. Vắng ⇒ 0 ⇒ flow im lặng ⇒ **0 regress** cho mọi
   * caller chưa nạp (kể cả sandbox preview, xem `orchestration-context.loader.ts`).
   */
  pendingInvitations: (ctx: OrchestrationContext): number => ctx.pendingInvitations ?? 0,
  /**
   * Tên nhóm được phép nêu (null = KHÔNG được nêu). Dùng `truthy`/`falsy` để **hai flow lời mời loại
   * trừ nhau bằng chính dữ liệu**, không cần luật triệt tiêu tay — và nhờ vậy chốt PII nằm ở ĐIỀU
   * KIỆN của flow, chỗ review được, thay vì nằm trong một nhánh `if` giữa tầng render.
   */
  namedInvitationGroup: (ctx: OrchestrationContext): string | null =>
    ctx.pendingInvitationGroupName ?? null,
  myPendingJoinRequests: (ctx: OrchestrationContext): number => ctx.myPendingJoinRequests ?? 0,
  /**
   * 🔴 US-AI-0441-S02 (@UC9697) — hai bậc cuối của thang khởi tạo. Loader ĐO (đếm cả ghế chưa nhận),
   * engine chỉ so sánh số. Vắng ⇒ 0 ⇒ flow im lặng ⇒ 0 regress.
   */
  soloManagedGroups: (ctx: OrchestrationContext): number => ctx.soloManagedGroups ?? 0,
  managedFundsWithoutCollection: (ctx: OrchestrationContext): number =>
    ctx.managedFundsWithoutCollection ?? 0,
  /**
   * 🔴 US-AI-0442-S02 (@UC9700) — nudge danh tính. Vắng ⇒ `false` ⇒ im lặng ⇒ **0 regress** cho mọi
   * caller chưa nạp (kể cả sandbox preview).
   */
  emailIdentityMissing: (ctx: OrchestrationContext): boolean => ctx.emailIdentityMissing === true,
  accountRecoveryAtRisk: (ctx: OrchestrationContext): boolean =>
    ctx.accountRecoveryAtRisk === true,
  /**
   * 🔴 US-AI-0442-S03 (@UC9701) — TÊN QUỸ đang mâu thuẫn (đã phát link công khai mà quỹ vẫn riêng tư);
   * `null` = không có mâu thuẫn nào. Dùng `truthy` để điều kiện của flow là DỮ LIỆU, và tên quỹ dùng
   * luôn cho câu chữ (không phải join lần hai).
   */
  publicLinkPrivateFund: (ctx: OrchestrationContext): string | null =>
    ctx.publicLinkPrivateFund ?? null,
  /**
   * 🔴 US-AI-0443-S03 (@UC9704) — số việc VỪA HOÀN TẤT. Loader đã lọc theo ngưỡng thời gian bằng
   * `now` của nó; engine chỉ đếm (DP-3 — engine không được đọc đồng hồ, nếu không admin preview và
   * app thật cho kết quả khác nhau chỉ vì chạy ở hai thời điểm).
   */
  recentlyCompletedCount: (ctx: OrchestrationContext): number =>
    (ctx.recentlyCompletedIntents ?? []).length,
  /** `null` ⇒ chưa đặt tên hiển thị. Tín hiệu đã có sẵn trong ctx từ 0295 ⇒ 0 truy vấn mới. */
  displayNameMissing: (ctx: OrchestrationContext): boolean =>
    (ctx.displayName ?? "").trim() === "",
  overdueCollectionsNoPayer: (ctx: OrchestrationContext): number =>
    ctx.overdueCollectionsNoPayer ?? 0,
  writeIntentDenied: (ctx: OrchestrationContext): boolean => ctx.writeIntentDenied === true,
  role: (ctx: OrchestrationContext): string | null => ctx.role,
  /**
   * 🔴 US-AI-0437-S04 (@UC9682) — số GHẾ có liên quan tới một nhóm THẬT (phụ huynh thứ hai, ghế
   * `unclaimed`, ghế vừa bị gỡ mà còn nợ). Có ghế ⇒ **không được** nói "bạn chưa có nhóm nào".
   * Vế này của 0437 tới nhánh 0439 dưới dạng DỮ LIỆU (`when[]` của flow NO_GROUP) thay vì một dòng
   * `&&` trong hàm rule — đúng thứ S03 hứa: thêm điều kiện chỉ sửa catalog.
   */
  affiliatedSeats: (ctx: OrchestrationContext): number => ctx.affiliatedSeats ?? 0,
});

export type SignalKey = keyof typeof SIGNALS;

/** Giá trị một tín hiệu có thể mang — cố ý hẹp để phép so sánh luôn tất định. */
export type SignalValue = number | string | boolean | null;

export function readSignal(key: SignalKey, ctx: OrchestrationContext): SignalValue {
  return SIGNALS[key](ctx);
}
